import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { FeedEvent } from '../types/social';

interface Args {
  userId: string;
  friendIds: string[];
  friendNamesById: Record<string, string>;
}

interface Result {
  events: FeedEvent[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Carrega últimos eventos do feed: dos amigos (audience aberto) + direcionados a mim.
 * Limit 30, últimos 14 dias.
 */
export function useFriendsFeed({ userId, friendIds, friendNamesById }: Args): Result {
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setEvents([]);
      setLoading(false);
      return;
    }
    if (friendIds.length === 0) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const sinceISO = new Date(Date.now() - FOURTEEN_DAYS_MS).toISOString();
    const friendList = friendIds.join(',');

    // Dois grupos: (autor é amigo AND audience null) OR audience = me
    // Supabase JS or() não suporta `and()` aninhado simples — fazemos 2 queries e mergeamos.
    const [{ data: publicFromFriends, error: e1 }, { data: targetedToMe, error: e2 }] = await Promise.all([
      supabase
        .from('feed_events')
        .select('id, user_id, event_type, payload, event_key, audience_user_id, created_at')
        .in('user_id', friendIds)
        .is('audience_user_id', null)
        .gte('created_at', sinceISO)
        .order('created_at', { ascending: false })
        .limit(30),
      supabase
        .from('feed_events')
        .select('id, user_id, event_type, payload, event_key, audience_user_id, created_at')
        .in('user_id', friendIds)
        .eq('audience_user_id', userId)
        .gte('created_at', sinceISO)
        .order('created_at', { ascending: false })
        .limit(30),
    ]);

    if (e1) console.error('useFriendsFeed e1', e1);
    if (e2) console.error('useFriendsFeed e2', e2);

    const merged: FeedEvent[] = [...(publicFromFriends ?? []), ...(targetedToMe ?? [])]
      .map((row) => ({
        ...(row as FeedEvent),
        author_name: friendNamesById[row.user_id] ?? 'Amigo',
      }))
      .sort((a, b) => (b.created_at < a.created_at ? -1 : 1))
      .slice(0, 30);

    // Dedup por id (pode haver overlap se RLS permitir os dois)
    const seen = new Set<string>();
    const deduped = merged.filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });

    setEvents(deduped);
    setLoading(false);
    // friendList unused; mantemos referência simbólica:
    void friendList;
  }, [userId, friendIds, friendNamesById]);

  useEffect(() => {
    load();
  }, [load]);

  return { events, loading, refresh: load };
}
